import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Radio as RadioIcon, Play, Pause, Volume2, VolumeX, Users, Send, MessageSquare, Settings, Trash2, Ban, Power, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { playTrack, useGlobalPlayer, stopPlayback } from '../components/GlobalMusicPlayer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================
// COMPONENTS DEFINED OUTSIDE
// ============================================

const GlassCard = ({ children, className = '' }) => (
  <div className={`bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl ${className}`}>
    {children}
  </div>
);

const ChatInput = ({ value, onChange, placeholder, ...props }) => (
  <input
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    className="flex h-10 w-full rounded-md border border-white/20 bg-black/50 px-3 py-2 text-base text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
    autoComplete="off"
    spellCheck="false"
    {...props}
  />
);

// ============================================
// MAIN COMPONENT
// ============================================

const Radio = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [metadata, setMetadata] = useState({ title: 'Loading...', artist: '', artwork: null, listeners: 0 });
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [nickname, setNickname] = useState('');
  const [showNicknamePrompt, setShowNicknamePrompt] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [radioPageChatEnabled, setRadioPageChatEnabled] = useState(true);
  const messagesEndRef = useRef(null);
  
  // Owner settings
  const [showSettings, setShowSettings] = useState(false);
  const [chatPageEnabled, setChatPageEnabled] = useState(true);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [confirmClear, setConfirmClear] = useState(false);
  
  const { isPlaying, isRadio } = useGlobalPlayer();
  const isRadioPlaying = isRadio && isPlaying;
  const isOwner = user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    fetchConfig();
    fetchMetadata();
    fetchMessages();
    fetchChatConfig();

    const metadataInterval = setInterval(fetchMetadata, 10000);
    const chatInterval = setInterval(fetchMessages, 5000);
    
    return () => {
      clearInterval(metadataInterval);
      clearInterval(chatInterval);
    };
  }, []);

  useEffect(() => {
    if (user) setNickname(user.username);
    else {
      const saved = localStorage.getItem('beatlab_nickname');
      if (saved) setNickname(saved);
    }
  }, [user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/radio/config`);
      setConfig(res.data);
      setChatEnabled(res.data.chat_enabled);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchChatConfig = async () => {
    try {
      const res = await axios.get(`${API}/chat/config`);
      setChatPageEnabled(res.data.chat_page_enabled ?? true);
      setRadioPageChatEnabled(res.data.radio_page_enabled ?? true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchMetadata = async () => {
    try {
      const res = await axios.get(`${API}/radio/metadata`);
      setMetadata(prev => {
        if (prev.title !== res.data.title || prev.artist !== res.data.artist) return res.data;
        return prev;
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/chat/messages?limit=50&source=radio`);
      setMessages(prev => {
        if (prev.length !== res.data.length) return res.data;
        if (prev.length > 0 && res.data.length > 0 && prev[prev.length-1]?.id !== res.data[res.data.length-1]?.id) return res.data;
        return prev;
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchBannedUsers = async () => {
    try {
      const token = localStorage.getItem('beatlab_token');
      const res = await axios.get(`${API}/chat/banned-users`, { headers: { Authorization: `Bearer ${token}` } });
      setBannedUsers(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleTogglePlay = () => {
    if (isRadioPlaying) {
      stopPlayback();
    } else {
      const streamUrl = `${process.env.REACT_APP_BACKEND_URL}/api/radio/stream`;
      playTrack({
        title: metadata.title || 'BeatLab52 Radio',
        artist: metadata.artist || 'Live Stream',
        stream_url: streamUrl,
        audio_url: streamUrl,
        thumbnail: metadata.artwork
      }, true);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!nickname.trim()) { setShowNicknamePrompt(true); return; }
    if (!newMessage.trim()) return;

    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.post(`${API}/chat/messages?source=radio`, { nickname, message: newMessage },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    }
  };

  const handleSetNickname = () => {
    if (nickname.trim()) {
      setShowNicknamePrompt(false);
      localStorage.setItem('beatlab_nickname', nickname);
    }
  };

  // Owner actions
  const handleDeleteMessage = async (id) => {
    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.delete(`${API}/chat/messages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      fetchMessages();
    } catch (error) { toast.error('Failed'); }
  };

  const handleBanUser = async (nick) => {
    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.post(`${API}/chat/ban/${nick}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Banned');
      fetchMessages();
      fetchBannedUsers();
    } catch (error) { toast.error('Failed'); }
  };

  const handleUnbanUser = async (nick) => {
    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.delete(`${API}/chat/ban/${nick}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Unbanned');
      fetchBannedUsers();
    } catch (error) { toast.error('Failed'); }
  };

  const handleClearChat = async () => {
    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.delete(`${API}/chat/messages`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cleared');
      setConfirmClear(false);
      fetchMessages();
    } catch (error) { toast.error('Failed'); }
  };

  const handleUpdateChatConfig = async (chatPage, radioPage) => {
    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.put(`${API}/chat/config`, { chat_page_enabled: chatPage, radio_page_enabled: radioPage },
        { headers: { Authorization: `Bearer ${token}` } });
      setChatPageEnabled(chatPage);
      setRadioPageChatEnabled(radioPage);
      toast.success('Updated');
    } catch (error) { toast.error('Failed'); }
  };

  const openSettings = () => { fetchBannedUsers(); setShowSettings(true); };
  const isChatAvailable = chatEnabled && radioPageChatEnabled;

  return (
    <div className="pt-4 pb-8">
      <div className="max-w-6xl mx-auto px-3 sm:px-6">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-wider mb-1 flex items-center gap-2"
            style={{ color: theme.primary, textShadow: theme.glow }} data-testid="radio-title">
            <RadioIcon className="w-6 h-6 sm:w-7 sm:h-7" /> Live Radio
          </h1>
          <p className="text-gray-400 text-xs sm:text-sm">Tune in to DJ808's live sessions</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Radio Player */}
          <div className="lg:col-span-2">
            <GlassCard className="p-6">
              <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-xl overflow-hidden flex-shrink-0"
                  style={{ boxShadow: isRadioPlaying ? theme.glow : 'none', border: `2px solid ${isRadioPlaying ? theme.primary : 'rgba(255,255,255,0.1)'}` }}>
                  {metadata.artwork ? (
                    <img src={metadata.artwork} alt="Now Playing" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-800 to-black flex items-center justify-center">
                      <RadioIcon className="w-12 h-12 text-gray-600" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Now Playing</div>
                  <h2 className="text-lg sm:text-2xl font-bold mb-1 truncate" style={{ color: theme.primary }} data-testid="now-playing-title">
                    {metadata.title || 'Unknown Track'}
                  </h2>
                  <p className="text-gray-400 text-sm sm:text-base truncate">{metadata.artist || 'Unknown Artist'}</p>
                  <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
                    <Users className="w-4 h-4" /> {metadata.listeners || 0} listeners
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Button size="lg" onClick={handleTogglePlay} className="w-16 h-16 rounded-full font-bold"
                  style={{ backgroundColor: theme.primary, color: '#000', boxShadow: theme.glow }} data-testid="radio-play-btn">
                  {isRadioPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
                </Button>
                <span className="text-gray-500 text-sm">Volume controlled by player bar below</span>
              </div>

              <div className="mt-8 h-24 bg-black/30 rounded-lg flex items-center justify-center overflow-hidden">
                {isRadioPlaying ? (
                  <div className="flex items-end gap-1 h-16">
                    {[...Array(20)].map((_, i) => (
                      <motion.div key={i} className="w-2 rounded-full" style={{ backgroundColor: theme.primary }}
                        animate={{ height: [10, Math.random() * 60 + 10, 10] }}
                        transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.05 }} />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 text-sm">Press play to start streaming</p>
                )}
              </div>
            </GlassCard>
          </div>

          {/* Chat */}
          <div className="lg:col-span-1">
            <GlassCard className="h-[600px] flex flex-col">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: theme.primary }}>
                  <MessageSquare className="w-5 h-5" /> Live Chat
                </h3>
                <div className="flex items-center gap-2">
                  {isOwner && (
                    <button onClick={openSettings} className="p-1.5 hover:bg-white/10 rounded" style={{ color: theme.primary }}
                      title="Settings" data-testid="radio-chat-settings-btn">
                      <Settings className="w-4 h-4" />
                    </button>
                  )}
                  {isChatAvailable && <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />}
                </div>
              </div>

              {isChatAvailable ? (
                <>
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-3">
                      {messages.map((msg) => (
                        <motion.div key={msg.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                          className="text-sm group flex items-start gap-1">
                          <div className="flex-1">
                            <span className="font-bold" style={{ color: theme.primary }}>{msg.nickname}: </span>
                            <span className="text-gray-300">{msg.message}</span>
                          </div>
                          {isOwner && (
                            <div className="opacity-0 group-hover:opacity-100 flex gap-0.5">
                              <button onClick={() => handleDeleteMessage(msg.id)} className="p-1 text-red-400"><Trash2 className="w-3 h-3" /></button>
                              <button onClick={() => handleBanUser(msg.nickname)} className="p-1 text-yellow-400"><Ban className="w-3 h-3" /></button>
                            </div>
                          )}
                        </motion.div>
                      ))}
                      {messages.length === 0 && <p className="text-gray-500 text-center py-8">No messages yet</p>}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {showNicknamePrompt && !user ? (
                    <div className="p-4 border-t border-white/10">
                      <p className="text-sm text-gray-400 mb-2">Enter a nickname:</p>
                      <div className="flex gap-2">
                        <ChatInput value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="Your nickname" data-testid="nickname-input" />
                        <Button onClick={handleSetNickname} style={{ backgroundColor: theme.primary, color: '#000' }} data-testid="set-nickname-btn">Set</Button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10">
                      <div className="flex gap-2">
                        <ChatInput value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                          placeholder={`Chat as ${nickname || 'Guest'}...`} maxLength={500} data-testid="chat-input" />
                        <Button type="submit" size="icon" style={{ backgroundColor: theme.primary, color: '#000' }} data-testid="send-message-btn">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                      {!nickname && !user && (
                        <button type="button" onClick={() => setShowNicknamePrompt(true)}
                          className="text-xs mt-2 hover:underline" style={{ color: theme.secondary }}>Add Nickname</button>
                      )}
                    </form>
                  )}
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-gray-500 text-center px-4">Chat is disabled</p>
                </div>
              )}
            </GlassCard>
          </div>
        </div>
      </div>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-black/95 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: theme.primary }}>
              <Settings className="w-5 h-5" /> Chat Settings
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="space-y-4 p-4 rounded-lg bg-white/5">
              <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2"><Power className="w-4 h-4" /> Controls</h3>
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-white">Chat Page</p></div>
                <Switch checked={chatPageEnabled} onCheckedChange={(c) => handleUpdateChatConfig(c, radioPageChatEnabled)} />
              </div>
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-white">Radio Chat</p></div>
                <Switch checked={radioPageChatEnabled} onCheckedChange={(c) => handleUpdateChatConfig(chatPageEnabled, c)} />
              </div>
            </div>

            <div className="space-y-3 p-4 rounded-lg bg-white/5">
              <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2"><Trash2 className="w-4 h-4" /> Clear</h3>
              {!confirmClear ? (
                <Button variant="outline" onClick={() => setConfirmClear(true)} className="w-full border-red-500/50 text-red-400">
                  <Trash2 className="w-4 h-4 mr-2" /> Clear All
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setConfirmClear(false)} className="flex-1">Cancel</Button>
                  <Button onClick={handleClearChat} className="flex-1 bg-red-500 text-white">Confirm</Button>
                </div>
              )}
            </div>

            <div className="space-y-3 p-4 rounded-lg bg-white/5">
              <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2"><Ban className="w-4 h-4" /> Banned ({bannedUsers.length})</h3>
              {bannedUsers.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {bannedUsers.map((nick) => (
                    <div key={nick} className="flex items-center justify-between p-2 bg-white/5 rounded">
                      <span className="text-sm text-gray-300">{nick}</span>
                      <Button size="sm" variant="ghost" onClick={() => handleUnbanUser(nick)} className="text-green-400 h-7 px-2">Unban</Button>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-gray-500">No banned users</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Radio;
